const ALPHA = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];
export const normalizeSize = value => String(value).trim().toUpperCase().replace(/^2XL$/, 'XXL').replace(/^3XL$/, 'XXXL');
export function lengthCm(value, unit = '') {
  const match = String(value).trim().match(/^(\d+(?:[.,]\d+)?)\s*(cm|mm|inches|inch|in|["″])?$/i);
  if (!match) return null;
  const suffix = (match[2] || unit).toLowerCase();
  const number = Number(match[1].replace(',', '.'));
  if (!number || !suffix) return null;
  if (['in', 'inch', 'inches', '"', '″'].includes(suffix)) return number * 2.54;
  return suffix === 'mm' ? number / 10 : suffix === 'cm' ? number : null;
}
export function braceletScale(config) {
  const rules = config.rules || {};
  const lengths = config.sizes.map(size => lengthCm(size.label, rules.unit)).filter(value => value !== null);
  if (!lengths.length) return null;
  const inch = /(?:inches|inch|in|["″])$/i;
  const unit = config.sizes.every(size => inch.test(String(size.label).trim()) || (!/[a-z"″]/i.test(size.label) && inch.test(rules.unit || ''))) ? 'in' : 'cm';
  return { min: Math.min(...lengths), max: Math.max(...lengths), unit };
}
export function chainChoices(config) {
  const offered = config.sizes.map(size => size.label);
  const sizes = config.profile?.answer_sizes || [];
  const descriptions = config.profile?.answer_descriptions || [];
  if (sizes.length !== descriptions.length) return [];
  const custom = sizes.length ? sizes.map((size, i) => ({size, label: descriptions[i]})) : config.rules?.chain_choices || [];
  return custom.filter(choice => typeof choice.label === 'string' && choice.label.trim() && offered.includes(choice.size)).map(choice => ({label: choice.label, size: choice.size}));
}
export function footwearChoices(config) {
  const system = config.rules?.variant_system;
  return (config.rules?.footwear_conversion || []).filter(row =>
    recommend({...config, rules: {...config.rules, width_rules: {}}}, {shoe: String(row[system]), shoe_system: system}).label
  );
}
export function recommend(config, answers) {
  const rules = {...(config.rules || {})};
  if (config.profile?.height_weight_bands?.length) rules.bands = config.profile.height_weight_bands;
  const offered = config.sizes.map(size => size.label);
  const findLabel = value => offered.find(label => normalizeSize(label) === normalizeSize(value));
  if (config.mode === 'jewelry') return { label: findLabel(answers.choice), basis: 'length' };
  if (config.mode === 'footwear') {
    if (Array.isArray(rules.footwear_conversion) && rules.footwear_conversion.length) {
      const rows = rules.footwear_conversion;
      const system = rules.variant_system;
      const inputSystem = answers.shoe_system || 'uk';
      const match = rows.filter(row => String(row[inputSystem]) === String(answers.shoe));
      const widthRule = rules.width_rules?.[answers.width];
      const widthOffset = Number(widthRule?.adjustment || 0);
      const offset = Number(config.adjustment || 0) + widthOffset;
      if (!['us', 'uk', 'eu'].includes(system) || match.length !== 1 || !Number.isInteger(offset)) return {};
      // Corrections move along the configured chart, never skip missing sizes to force a fit.
      const target = rows[rows.indexOf(match[0]) + offset];
      return target ? {label: findLabel(target.size ?? target[system]), basis: 'based_usual'} : {};
    }
    const p = config.profile || {}, sizes = p.footwear_sizes || [], mins = p.footwear_min || [], maxs = p.footwear_max || [];
    const length = Number(answers.foot);
    if (!p.footwear_ready || !length || !sizes.length || sizes.length !== mins.length || sizes.length !== maxs.length) return {};
    const rows = sizes.map((size,i)=>({size,min:Number(mins[i]),max:Number(maxs[i])}));
    if (rows.some(row=>!Number.isFinite(row.min)||!Number.isFinite(row.max)||row.min<=0||row.max<row.min)) return {};
    const matches = rows.filter(row=>length>=row.min&&length<=row.max);
    return matches.length === 1 ? {label:findLabel(matches[0].size),basis:'based_foot'} : {};
  }
  if (config.mode === 'ring') {
    const profile = config.profile || {};
    const sizes = profile.ring_sizes || [], diameters = profile.ring_diameters || [];
    const diameter = Number(answers.ring);
    const tolerance = Number(profile.ring_tolerance || 0.2);
    if (!profile.ring_ready || !diameter || sizes.length !== diameters.length || !sizes.length || !(tolerance > 0 && tolerance <= 0.5)) return {};
    const rows = sizes.map((size, i) => ({size, diameter: Number(diameters[i])}));
    if (rows.some(row => !Number.isFinite(row.diameter) || row.diameter <= 0)) return {};
    const matches = rows.map(row => ({...row, distance: Math.abs(row.diameter - diameter)})).filter(row => row.distance <= tolerance + 1e-9).sort((a,b) => a.distance - b.distance);
    if (!matches.length || (matches[1] && Math.abs(matches[0].distance - matches[1].distance) < 1e-9)) return {};
    return {label: findLabel(matches[0].size), basis: 'based_ring'};
  }
  if (config.mode === 'bracelet') {
    const wrist = Number(answers.wrist);
    const allowance = Number((rules.allowance_cm || { tight: 0.5, regular: 1, loose: 1.5 })[answers.fit || 'regular']);
    if (!Number.isFinite(wrist) || wrist < 5 || wrist > 50 || !Number.isFinite(allowance) || allowance < 0) return {};
    const options = offered.map(label => ({ label, cm: lengthCm(label, rules.unit) })).filter(o => o.cm !== null).sort((a, b) => a.cm - b.cm);
    const target = wrist + allowance;
    const selected = options.find(o => o.cm + 0.001 >= target);
    if (!selected || selected.cm - target > Number(rules.max_extra_cm ?? 2)) return {};
    return { label: selected.label, basis: 'based_wrist' };
  }
  let baseline = answers.usual;
  let basis = 'based_usual';
  let order = rules.size_order || (offered.every(label => ALPHA.includes(normalizeSize(label))) ? ALPHA : offered);
  if (Array.isArray(rules.rows) && rules.rows.length) {
    const measurements = rules.measurements || [];
    if (!measurements.length || !measurements.every(key => Number(answers[key]) > 0)) return {};
    const row = rules.rows.find(row => measurements.every(key => {
      const range = row[key];
      if (!Array.isArray(range) || range.length !== 2) return false;
      const measured = Number(answers[key]);
      const ease = rules.measurement_type === 'garment' ? Number(rules.ease_cm?.[answers.fit || 'regular']?.[key]) : 0;
      return Number.isFinite(ease) && measured + ease >= Number(range[0]) && measured + ease <= Number(range[1]);
    }));
    if (!row) return {};
    baseline = row.size;
    order = rules.size_order || rules.rows.map(r => r.size);
    basis = 'based_chart';
  } else if (Array.isArray(rules.bands) && rules.bands.length) {
    const candidates = rules.bands.filter(b => ['height', 'weight'].every(k => Array.isArray(b[k]) && Number(answers[k]) >= b[k][0] && Number(answers[k]) <= b[k][1]));
    if (!candidates.length) return {};
    const preferred = candidates.find(b => normalizeSize(b.size) === normalizeSize(answers.usual));
    if (!preferred && candidates.length > 1) return {};
    baseline = (preferred || candidates[0]).size;
    basis = 'based_bands';
  }
  const index = order.map(normalizeSize).indexOf(normalizeSize(baseline));
  if (index < 0) return {};
  const fitOffset = basis === 'based_chart' && rules.measurement_type === 'garment' ? 0 : Number((rules.fit_offsets || { tight: -1, regular: 0, loose: 1 })[answers.fit || 'regular']);
  const adjustment = rules.adjustment_included ? 0 : Number(config.adjustment || 0);
  if (![fitOffset, adjustment].every(Number.isInteger)) return {};
  const target = order[index + adjustment + fitOffset];
  return target ? { label: findLabel(target), basis } : {};
}

const create = (tag, classes = '', text = '') => {
  const node = document.createElement(tag);
  node.className = classes;
  node.textContent = text;
  return node;
};
class SizeGuideNote extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    this.refresh = () => {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.dataset.key)); } catch {}
      const valid = saved && saved.signature === this.dataset.signature && Date.now() - saved.time < 86400000;
      this.hidden = !valid;
      this.replaceChildren();
      if (valid) {
        const [before, after = ''] = this.dataset.message.split('[size]');
        this.append(document.createTextNode(before), create('strong', '', saved.label), document.createTextNode(after));
      }
    };
    this.refresh();document.addEventListener('size-guide:result', this.refresh);
  }
  disconnectedCallback() { document.removeEventListener('size-guide:result', this.refresh); }
}
class SizeGuide extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.initialized) return;
    try { this.config = JSON.parse(this.querySelector('[data-size-guide-config]').textContent); } catch { return; }
    if (!['clothing', 'bracelet', 'jewelry', 'ring', 'footwear'].includes(this.config.mode)) return;
    this.initialized = true;
    this.labels = this.config.labels;
    this.config.rules ||= {};
    if (this.config.profile?.height_weight_bands?.length) this.config.rules.bands = this.config.profile.height_weight_bands;
    this.answers = { fit: 'regular' };
    this.step = 0;
    this.stage = this.querySelector('[data-guide-stage]');
    this.status = this.querySelector('[data-guide-status]');
    this.steps = this.getSteps();
    this.abort = new AbortController();
    this.addEventListener('click', event => {
      const tab = event.target.closest('[data-guide-tab]');
      if (!tab) return;
      this.querySelector('[data-guide-finder]').hidden = tab.dataset.guideTab !== 'finder';
      this.querySelector('[data-guide-chart]').hidden = tab.dataset.guideTab !== 'chart';
      this.querySelectorAll('[data-guide-tab]').forEach(button => {
        const active = button === tab;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('button--outline', !active);
      });
    }, { signal: this.abort.signal });
    this.render();
  }
  disconnectedCallback() { this.abort?.abort(); this.initialized = false; }
  getSteps() {
    const rules = this.config.rules;
    let required;
    if (this.config.mode === 'footwear') required = [rules.footwear_conversion?.length ? 'shoe' : 'foot', ...(rules.width_question ? ['width'] : [])];
    else if (this.config.mode === 'ring') required = ['ring'];
    else if (this.config.mode === 'bracelet') required = ['wrist'];
    else if (this.config.mode === 'jewelry') required = [this.config.chainFit ? 'chain_fit' : 'choice'];
    else if (rules.rows?.length) required = [...(rules.measurements || []), 'fit'];
    else if (rules.bands?.length) required = ['usual', 'height', 'weight', 'fit'];
    else required = ['usual', ...(rules.height_question === false ? [] : ['height']), 'fit'];
    const order = [...(this.config.profile?.question_steps || [])];
    if (required.includes('height') && !order.includes('height') && order.includes('usual')) order.splice(order.indexOf('usual') + 1, 0, 'height');
    return [...new Set([...order.filter(key => required.includes(key)), ...required])];
  }
  questionTitle(key) {
    const profile = this.config.profile || {};
    const index = (profile.question_steps || []).indexOf(key);
    return profile.question_titles?.[index] || (['chain_fit','ring'].includes(key) && profile.question) || this.labels[key === 'choice' ? 'length' : key];
  }
  button(text, action, style = 'button') {
    const button = create('button', style, text);
    button.type = 'button';
    button.addEventListener('click', action);
    return button;
  }
  render() {
    this.renderStage();
    if (this.step > 0 && this.step < this.steps.length) this.stage.append(this.button(this.labels.back, () => { this.step--;this.render();this.focusStage(); }, 'link justify-self-center'));
  }
  renderStage() {
    this.stage.replaceChildren();
    this.stage.classList.remove('size-guide__result');
    this.renderProgress();
    this.status.textContent = '';
    const key = this.steps[this.step];
    if (!key) return this.showResult();

    const heading = create('h3', 'h5 text-center', this.questionTitle(key));
    this.stage.append(heading);
    if (key === 'height') return this.renderHeight();
    if (key === 'width') return this.renderWidth();
    if (key === 'shoe') return this.renderShoe();
    if (key === 'wrist') return this.renderWrist(heading);
    if (key === 'chain_fit') return this.renderChain();
    if (key === 'ring') { heading.remove();return this.renderRing(); }
    if (['usual', 'fit', 'choice'].includes(key)) {
      const choices = key === 'fit' ? ['tight', 'regular', 'loose'] : key === 'usual' ? this.config.sizes.map(s => s.label) : this.config.sizes.map(s => s.label);
      const group = create('div', 'size-guide__choices' + (key === 'fit' ? ' size-guide__fit-choices' : ''));
      for (const choice of choices) {
        const button = this.button(key === 'fit' ? this.labels[choice + '_fit'] : choice, () => {
          this.answers[key] = choice;
          if (key === 'choice') this.showImage(choice);
          this.step++;
          this.render();
          this.focusStage();
        }, key === 'fit' ? 'button size-guide__choice' + (this.answers.fit === choice ? '' : ' button--outline') : 'block-swatch size-guide__choice');
        button.setAttribute('aria-pressed', String(this.answers[key] === choice));
        if (key === 'fit' && this.steps.filter(step => step !== 'fit').every(step => this.answers[step] != null)) button.disabled = !recommend(this.config, {...this.answers, fit: choice}).label;
        group.append(button);
      }
      this.stage.append(group);
    } else {
      if (key === 'foot') this.stage.append(create('p','text-subdued',this.labels.foot_help));
      const ranges = { foot: [5, 45], weight: [30, 250], bust: [50, 200], waist: [40, 200], hips: [50, 220], inseam: [30, 130] };
      const [min, max] = ranges[key] || [1, 250];
      const id = `${this.closest('x-modal').id}-${key}`;
      const label = create('label', 'sr-only', this.labels[key]); label.htmlFor = id;
      const field = create('input', 'input');field.type = 'number';field.id = id;field.min = min;field.max = max;field.step = '0.1';field.required = true;field.inputMode = 'decimal';
      field.value = this.answers[key] ?? '';
      this.stage.append(label, field);
      const next = () => { if (!field.reportValidity()) return;this.answers[key] = Number(field.value);this.step++;this.render();this.focusStage(); };
      field.addEventListener('keydown', event => { if(event.key === 'Enter') { event.preventDefault();next(); } });
      this.stage.append(this.button(this.labels.next, next));
    }
  }
  renderHeight() {
    this.answers.height ??= 170.18;
    const min = 120, max = 220;
    const id = `${this.closest('x-modal').id}-height`;
    const readout = create('div', 'v-stack gap-2 text-center');
    const metric = create('span', 'text-subdued');
    const output = create('output', 'h3');output.htmlFor = id;
    readout.append(metric, output);
    const slider = create('input', 'range size-guide__range');
    Object.assign(slider, {id, type:'range', min, max, step:'any', value:this.answers.height});
    slider.setAttribute('aria-label', this.labels.height);
    const sync = () => {
      this.answers.height = Number(slider.value);
      const inches = Math.round(this.answers.height / 2.54);
      output.textContent = `${Math.floor(inches / 12)} ft ${inches % 12} in`;
      metric.textContent = `${Math.round(this.answers.height)} cm`;
      slider.setAttribute('aria-valuetext', `${output.textContent}, ${metric.textContent}`);
      slider.style.setProperty('--size-guide-progress', `${(this.answers.height-min)/(max-min)*100}%`);
    };
    slider.addEventListener('input', sync);
    slider.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowDown','ArrowRight','ArrowUp','Home','End'].includes(event.key)) return;
      event.preventDefault();const direction = ['ArrowLeft','ArrowDown'].includes(event.key) ? -1 : 1;
      slider.value = event.key === 'Home' ? min : event.key === 'End' ? max : Math.max(min, Math.min(max, (Math.round(this.answers.height / 2.54) + direction) * 2.54));sync();
    });
    sync();
    this.stage.append(readout, slider, this.button(this.labels.next, () => { this.step++;this.render();this.focusStage(); }));
  }
  renderShoe() {
    const systems = this.config.rules.display_systems || ['uk', 'eu'];
    const unit = this.shoeUnit || systems[0];
    const tabs = create('div', 'h-stack justify-center gap-3');
    for (const system of systems) {
      const button = this.button(system.toUpperCase(), () => { this.shoeUnit = system;this.render(); }, `button button--sm${unit === system ? '' : ' button--outline'}`);
      button.setAttribute('aria-pressed', String(unit === system));tabs.append(button);
    }
    const choices = create('div', 'size-guide__choices');
    for (const row of footwearChoices(this.config)) {
      const button = this.button(String(row[unit]), () => {
        this.answers.shoe = String(row[this.config.rules.variant_system]);this.answers.shoe_system = this.config.rules.variant_system;this.step++;this.render();this.focusStage();
      }, 'block-swatch size-guide__choice');
      button.setAttribute('aria-label', `${unit.toUpperCase()} ${row[unit]}`);
      choices.append(button);
    }
    this.stage.append(tabs, choices);
  }
  renderWidth() {
    const group = create('div', 'size-guide__choices');
    for (const width of ['narrow', 'regular', 'wide']) {
      const button = this.button('', () => { this.answers.width = width;this.step++;this.render();this.focusStage(); }, 'block-swatch v-stack gap-3 size-guide__width-choice');
      const img = create('img');img.src = this.config.widthImages[width];img.alt = '';img.width = 90;img.height = 132;
      button.append(img, create('span', '', this.labels[width]));group.append(button);
    }
    this.stage.append(group);
  }
  renderProgress() {
    let progress = this.querySelector('[data-guide-progress]');
    if (!progress) { progress = create('div', 'v-stack gap-2');progress.dataset.guideProgress = '';this.querySelector('[data-guide-finder]').append(progress); }
    progress.replaceChildren();progress.hidden = this.steps.length < 2 || this.step >= this.steps.length;
    if (progress.hidden) return;
    const current = Math.min(this.step, this.steps.length - 1);
    const description = this.labels.step.replace('{current}', current + 1).replace('{total}', this.steps.length);
    progress.append(create('p', 'text-sm text-subdued text-center', description));
  }
  saveResult(label) {
    try {
      if (label) sessionStorage.setItem(this.dataset.resultKey, JSON.stringify({ label, signature: this.dataset.signature, time: Date.now() }));
      else sessionStorage.removeItem(this.dataset.resultKey);
    } catch {}
    document.dispatchEvent(new CustomEvent('size-guide:result'));
  }
  renderChain() {
    const choices = chainChoices(this.config);
    if (!choices.length) { this.status.textContent = this.labels.unavailable;return; }
    const group = create('div', 'size-guide__choices');
    for (const choice of choices) {
      const button = this.button('', () => { this.answers.choice = choice.size;this.step++;this.render();this.focusStage(); }, 'block-swatch size-guide__choice v-stack gap-2');
      const image = [...this.querySelectorAll('[data-guide-image]')].find(figure => figure.dataset.guideImage === choice.size)?.querySelector('img');
      if (image) { const preview = image.cloneNode();preview.classList.add('size-guide__choice-image');preview.alt = '';button.append(preview); }
      button.append(create('span', '', choice.label));group.append(button);
    }
    this.stage.append(group);
  }
  renderRing() {
    const profile = this.config.profile || {};
    const sizes = profile.ring_sizes || [], diameters = profile.ring_diameters || [];
    const offered = new Set(this.config.sizes.map(size => size.label));
    const measurements = sizes.map((size, i) => offered.has(size) ? Number(diameters[i]) : NaN).filter(value => Number.isFinite(value) && value > 0);
    if (!profile.ring_ready || sizes.length !== diameters.length || !measurements.length) { this.status.textContent = this.labels.unavailable;return; }
    this.ringMethod ||= 'circumference';this.ringUnit ||= 'cm';
    const methods = create('div', 'h-stack justify-center gap-3');
    for (const method of ['circumference', 'diameter']) {
      const button = this.button(this.labels[method], () => { this.ringMethod = method;this.render();this.focusStage(); }, 'button button--sm' + (method === this.ringMethod ? '' : ' button--outline'));
      button.setAttribute('aria-pressed', String(method === this.ringMethod));methods.append(button);
    }
    const min = Math.min(...measurements), max = Math.max(...measurements);
    const factor = (this.ringMethod === 'diameter' ? 1 : Math.PI) / (this.ringUnit === 'cm' ? 10 : 25.4);
    const format = diameter => `${Number((diameter * factor).toFixed(2))} ${this.ringUnit}`;
    const units = create('div', 'h-stack justify-center gap-3');
    for (const unit of ['in','cm']) {
      const button = this.button(unit, () => { this.ringUnit = unit;this.render();this.focusStage(); }, 'button button--sm' + (unit === this.ringUnit ? '' : ' button--outline'));
      button.setAttribute('aria-pressed', String(unit === this.ringUnit));units.append(button);
    }
    const id = `${this.closest('x-modal').id}-ring`;
    const current = create('output', 'h4 text-center');current.htmlFor = id;
    const slider = create('input', 'range size-guide__range');
    Object.assign(slider,{id,type:'range',min:String(min),max:String(max),step:'any',value:String(Math.max(min,Math.min(max,this.answers.ring ?? measurements[Math.floor(measurements.length/2)])))});
    slider.setAttribute('aria-label', `${this.labels[this.ringMethod]} (${this.ringUnit})`);
    const sync = () => {
      this.answers.ring = Number(slider.value);current.textContent = format(this.answers.ring);
      slider.setAttribute('aria-valuetext',format(this.answers.ring));
      slider.style.setProperty('--size-guide-progress', `${max === min ? 100 : (this.answers.ring-min)/(max-min)*100}%`);
    };
    slider.addEventListener('input',sync);
    slider.addEventListener('keydown',event=>{
      if (!['ArrowLeft','ArrowDown','ArrowRight','ArrowUp','Home','End'].includes(event.key)) return;
      event.preventDefault();const direction = ['ArrowLeft','ArrowDown'].includes(event.key) ? -1 : 1;
      slider.value = String(event.key === 'Home' ? min : event.key === 'End' ? max : Math.max(min,Math.min(max,Number(slider.value)+direction*0.1)));sync();
    });
    slider.disabled = min === max;sync();
    const endpoints = create('div','size-guide__actions text-sm text-subdued');endpoints.append(create('span','',format(min)),create('span','',format(max)));
    const control = create('div','size-guide__slider-control');control.append(slider,endpoints);
    const next = () => { this.step++;this.render();this.focusStage(); };
    this.stage.append(methods,units,create('p','text-subdued text-center',this.labels[this.ringMethod+'_help']),current,control,this.button(this.labels.find,next));
  }
  renderWrist(heading) {
    const scale = braceletScale(this.config);
    if (!scale) { this.status.textContent = this.labels.unavailable; return; }
    this.wristUnit ||= scale.unit;
    const factor = this.wristUnit === 'in' ? 2.54 : 1;
    const format = value => `${Number(value.toFixed(2))} ${this.wristUnit}`;
    heading.textContent = this.questionTitle('wrist');
    const units = create('div', 'h-stack justify-center gap-3');
    for (const unit of ['in', 'cm']) {
      const button = this.button(unit, () => { this.wristUnit = unit;this.render();this.focusStage(); }, 'button button--sm' + (unit === this.wristUnit ? '' : ' button--outline'));
      button.setAttribute('aria-pressed', String(unit === this.wristUnit));
      units.append(button);
    }
    this.stage.append(units, create('p', 'text-subdued text-center', this.labels.measure_wrist));
    const min = Number((scale.min / factor).toFixed(2)), max = Number((scale.max / factor).toFixed(2));
    const stops = [min];
    for (let value = Math.ceil(min * 2) / 2; value < max; value += 0.5) if (value > min) stops.push(value);
    if (max > min) stops.push(max);
    const wanted = (this.answers.wrist ?? (scale.min + scale.max) / 2) / factor;
    const index = stops.reduce((best, value, i) => Math.abs(value - wanted) < Math.abs(stops[best] - wanted) ? i : best, 0);
    const id = `${this.closest('x-modal').id}-wrist`;
    const current = create('output', 'h4 text-center');current.htmlFor = id;
    const slider = create('input', 'range size-guide__range');
    Object.assign(slider, { type: 'range', id, min: '0', max: String(stops.length - 1), step: 'any', value: String(index) });
    slider.setAttribute('aria-label', `${this.labels.wrist} (${this.wristUnit})`);
    const sync = () => {
      const position = Number(slider.value);
      const lower = Math.floor(position), upper = Math.ceil(position);
      const value = stops[lower] + (stops[upper] - stops[lower]) * (position - lower);
      this.answers.wrist = value * factor;
      current.textContent = format(stops[Math.round(position)]);
      slider.setAttribute('aria-valuetext', format(value));
      slider.style.setProperty('--size-guide-progress', `${max === min ? 100 : Number(slider.value) / (stops.length - 1) * 100}%`);
    };
    const settle = () => { slider.value = String(Math.round(Number(slider.value)));sync(); };
    slider.addEventListener('change', settle);
    slider.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const direction = ['ArrowLeft', 'ArrowDown'].includes(event.key) ? -1 : 1;
      slider.value = String(event.key === 'Home' ? 0 : event.key === 'End' ? stops.length - 1 : Math.max(0, Math.min(stops.length - 1, Math.round(Number(slider.value)) + direction)));sync();
    });
    slider.disabled = stops.length === 1;
    slider.addEventListener('input', sync);sync();
    const endpoints = create('div', 'size-guide__actions text-sm text-subdued');
    endpoints.append(create('span', '', format(min)), create('span', '', format(max)));
    const next = () => { settle();this.step++;this.render();this.focusStage(); };
    const control = create('div', 'size-guide__slider-control');control.append(slider, endpoints);
    this.stage.append(current, control, this.button(this.labels.find, next));
  }
  focusStage() {
    this.stage.querySelector('input:not([type="range"]), button')?.focus({ preventScroll: true });
  }
  showImage(label) { this.querySelectorAll('[data-guide-image]').forEach(figure => { figure.hidden = figure.dataset.guideImage !== label; }); }
  currentVariant(label) {
    const form = document.getElementById(this.dataset.formId);
    const picker = [...document.querySelectorAll('variant-picker')].find(p => p.getAttribute('form-id') === this.dataset.formId);
    const current = picker?.selectedVariant;
    if (!current) return null;
    const position = Number(this.dataset.optionPosition) - 1;
    return this.config.variants.find(v => v.options.every((value, i) => value === (i === position ? label : current.options[i])));
  }
  showResult() {
    const result = recommend(this.config, this.answers);
    this.saveResult(result.label);
    this.stage.classList.add('size-guide__result');
    if (!result.label) {
      this.status.textContent = this.labels.outside;
    } else {
      this.stage.append(create('p', 'text-base', this.config.profile.result_message || this.labels.result), create('p', 'size-guide__size', result.label));
      const variant = this.currentVariant(result.label);
      const button = this.button(this.labels.select, () => this.apply(result.label));
      button.disabled = !variant?.available;
      if (!variant || !variant.available) this.status.textContent = this.labels[variant ? 'sold_out' : 'unavailable'];
      this.stage.append(button);
      this.showImage(result.label);
    }
    this.stage.append(this.button(this.labels.edit, () => { this.step = 0;this.stage.classList.remove('size-guide__result');this.render();this.focusStage(); }, 'link justify-self-center'));
  }
  async apply(label) {
    const form = document.getElementById(this.dataset.formId);
    const picker = [...document.querySelectorAll('variant-picker')].find(p => p.getAttribute('form-id') === this.dataset.formId);
    if (!form || !picker || !this.currentVariant(label)?.available) { this.status.textContent = this.labels.unavailable; return; }
    const target = this.config.sizes.find(s => s.label === label);
    const checked = [...form.elements].filter(input => input.matches('input[data-option-position]:checked')).sort((a,b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    const values = checked.map(input => Number(input.dataset.optionPosition) === Number(this.dataset.optionPosition) ? String(target.id) : input.value);
    if (!target?.id || values.length !== picker.selectedVariant.options.length) { this.status.textContent = this.labels.error; return; }
    const modal = this.closest('x-modal');
    this.stage.querySelectorAll('button').forEach(button => { button.disabled = true; });
    try { await picker.selectCombination({ optionValues: values, productChange: false });await modal.hide(); }
    catch { this.status.textContent = this.labels.error;this.stage.querySelectorAll('button').forEach(button => { button.disabled = false; }); }
  }
}
if (globalThis.customElements && !customElements.get('size-guide')) customElements.define('size-guide', SizeGuide);

if (globalThis.customElements && !customElements.get('size-guide-note')) customElements.define('size-guide-note', SizeGuideNote);

